import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { ArrowUpToLine } from 'lucide-react';
import { decodeJson, encodeJson, StringParam, withDefault } from 'use-query-params';
import isEqual from 'lodash/isEqual';
import { useFlags } from '@flagsmith/flagsmith/react';

import Page from 'src/components/page/PageV2';
import KeyListener from 'src/pages/landlord/conversations/components/key-listener';
import Header from 'src/pages/landlord/conversations/components/header';
import StageTabs from 'src/pages/landlord/conversations/components/navigation';
import List from 'src/pages/landlord/conversations/components/list';
import Toolbar from 'src/pages/landlord/conversations/components/toolbar';
import ConversationsHotkeysFooter from 'src/pages/landlord/conversations/components/hotkeys';
import MobileMenu from 'src/pages/landlord/conversations/components/mobile-menu';
import EmptyConversationsList from 'src/pages/landlord/conversations/components/empty/EmptyConversationsList';
import { useConversations, useProperties, type ELeadStage } from 'src/services/api';
import { GroupByOption } from 'src/pages/landlord/conversations/components/header/GroupBySelector';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useTypedQueryParam } from 'src/hooks/use-typed-query-param';
import useCurrentInbox from 'src/services/api/inboxes/use-current-inbox';
import { useAllInboxes } from 'src/services/api/inboxes/use-all-inboxes';
import type { IConversation } from 'src/api/landlord-messages-api';
import {
    EConversationTab,
    conversationTabs,
    type IConversationMatchWithChannel,
    conversationCategories,
    EConversationCategory,
} from 'src/pages/landlord/conversations/context/types';
import { getConversationsByCategory } from 'src/pages/landlord/conversations/utils/get-conversations-by-category';
import { useOpenConversation } from 'src/pages/landlord/conversations/context/hooks/use-open-conversation';
import ConversationDrawer from 'src/pages/landlord/conversations/components/drawer/ConversationDrawer';
import { useConversationDrawer, useConversationDrawerFallbackRedirect } from 'src/pages/landlord/conversations/components/drawer/use-conversation-drawer';
import { filterConversations, omitNullsFromInboxFilter } from 'src/pages/landlord/conversations/utils/filter-conversations';
import { mergeNarrowedFilters } from 'src/pages/landlord/conversations/utils/filter-narrowing';
import { analyticsClient, AnalyticsEvent } from 'src/services/analytics';
import useTeamMembers from 'src/services/api/team/members/use-team-members';
// will create a separate PR to take off all feature flag for team availability
import { EFeatureFlag, FORCE_TEAM_AVAILABILITY } from 'src/utils/feature-flags';
import { useUnreadFromOfflineTeammatePredicate } from 'src/services/api/messages/use-unread-from-offline-teammates';

import styles from 'src/pages/landlord/conversations/ConversationsInboxPage.module.css';

export const INITIAL_FILTERS: {
    assigneeIds: string[];
    ingestionSources: string[];
    leadStages: ELeadStage[];
    leadLabelIds: string[];
    excludeLeadLabelIds: string[];
    propertyIds: string[];
    hasNoProperty: boolean;
    propertyLabelIds: string[];
} = {
    assigneeIds: [],
    ingestionSources: [],
    leadStages: [],
    leadLabelIds: [],
    excludeLeadLabelIds: [],
    propertyIds: [],
    hasNoProperty: false,
    propertyLabelIds: [],
};

export default function ConversationsInboxPage() {
    // Scroll to top button state
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isHovering, setIsHovering] = useState(false);

    // UI state
    const [selectedConversations, setSelectedConversations] = useState<(IConversation | IConversationMatchWithChannel)[]>([]);
    const [focusedConversation, setFocusedConversation] = useState<IConversation | undefined>(undefined);
    const focusedConversationRef = useRef<IConversation | undefined>(undefined);
    const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<HTMLElement>();
    const [isFilterOpen, setFilterOpen] = useState(false);
    const [groupBy, setGroupBy] = useTypedQueryParam<GroupByOption>('groupby', {
        encode: (v) => v === GroupByOption.FRESHNESS ? undefined : v,
        decode: (s) => Object.values(GroupByOption).includes(s as GroupByOption) ? s as GroupByOption : GroupByOption.FRESHNESS,
    });

    // Get server data for loading check and openConversation function
    const { loggedInUser, leads, leadsByUuid } = useAuthenticatedState();
    const { data: conversations } = useConversations();
    const { data: properties } = useProperties();
    const { inbox, isDefaultInbox } = useCurrentInbox();
    const { data: allInboxes } = useAllInboxes();
    const { data: teamMembers } = useTeamMembers();
    const { id: inboxRouteId } = useParams<{ id: string }>();

    const flags = useFlags([EFeatureFlag.TEAM_AVAILABILITY]);
    const showPresence = Boolean(flags.team_availability?.enabled) || FORCE_TEAM_AVAILABILITY;

    const isUnreadFromOfflineTeammate = useUnreadFromOfflineTeammatePredicate({
        teamMembers,
        currentUserId: loggedInUser?.id,
        enabled: Boolean(showPresence && inbox?.is_system),
    });
    // On a specific inbox route the inbox's saved filters must be applied before any conversation
    // is shown
    const isInboxResolved = !inboxRouteId || allInboxes !== undefined;

    // URL query params for tab, search, filter
    const [tab, setTab] = useTypedQueryParam<EConversationTab>('tab', {
        encode: (t) => t,
        decode: (s) => {
            if (conversationTabs.includes(s as EConversationTab)) {
                return s as EConversationTab;
            }
            return EConversationTab.ALL;
        }
    });

    const [search, setSearch] = useTypedQueryParam<string>('search', withDefault(StringParam, ''));
    const [filter, _setFilter] = useTypedQueryParam<any>('filter', {
        encode: (value) => {
            if (value == null) {
                return value;
            }
            if (typeof value === 'object') {
                const withoutDefaults = Object.entries(value).reduce((acc, [key, val]) => {
                    if (key in INITIAL_FILTERS && isEqual(INITIAL_FILTERS[key as keyof typeof INITIAL_FILTERS], val)) {
                        return acc;
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    acc[key] = val;
                    return acc;
                }, {} as Partial<typeof INITIAL_FILTERS>);
                return encodeJson(withoutDefaults);
            }
            return encodeJson(value);
        },
        decode: (value) => {
            let decoded = decodeJson(value);
            if (typeof decoded === 'object' && decoded != null) {
                // explicitly fix null -> undefined (in case of backend inconsistencies)
                decoded = omitNullsFromInboxFilter(decoded);

                return {
                    ...INITIAL_FILTERS,
                    ...decoded,
                };
            }
            return INITIAL_FILTERS;
        },
    });

    // `filter` is the user's narrowing picks applied on top of the current inbox's saved filters.
    // It is stored separately from the inbox filters (not pre-merged), so the modal and the chip
    // row under the search bar show only what the user added, while the inbox's own filters are
    // shown read-only beside the title. The effective filter applied to the list is computed by
    // mergeNarrowedFilters below.
    const setFilter = useCallback((newFilter: Partial<typeof INITIAL_FILTERS>) => {
        const _newFilter = { ...filter, ...newFilter };
        _setFilter(_newFilter);
    }, [filter, _setFilter]);

    const resetFilter = useCallback(() => {
        _setFilter(INITIAL_FILTERS);
    }, [_setFilter]);

    const effectiveFilter = useMemo(
        () => mergeNarrowedFilters(inbox?.filters, filter),
        [inbox, filter],
    );

    // Get hidden lead label IDs
    const selectHiddenLeadLabelIds = useCallback(
        (settings: any) => settings?.conversations_hidden_lead_label_ids ?? undefined,
        []
    );
    const hiddenLeadLabelIds = loggedInUser?.settings ? selectHiddenLeadLabelIds(loggedInUser.settings) : undefined;

    // Reset selected conversations when tab, search or filter changes
    useEffect(() => {
        setSelectedConversations([]);
        setFocusedConversation(undefined);
        focusedConversationRef.current = undefined;
    }, [tab, search, filter]);

    // Keep ref in sync with state for getFocusedConversation callback
    useEffect(() => {
        focusedConversationRef.current = focusedConversation;
    }, [focusedConversation]);

    // Update selected conversations when conversations data changes
    useEffect(() => {
        if (conversations === undefined) {
            return;
        }

        const conversationsById = new Map(conversations.map(o => [o.lead_uuid, o]));
        setSelectedConversations(prev => {
            return prev.map(o => {
                return conversationsById.get(o.lead_uuid) || o;
            });
        });
    }, [conversations]);

    const filteredConversations = useMemo(() => {
        if (!properties || !conversations || !leadsByUuid) {
            return [];
        }
        if (search) {
            const inboxName = isDefaultInbox ? 'Default Inbox' : inbox?.name;
            // TODO: turn this off after a while - just want to get a sense of what people are searching for
            analyticsClient.track(AnalyticsEvent.LOCAL_INBOX_SEARCH, {
                inboxName,
                search,
            });
        }

        return filterConversations({
            hiddenLeadLabelIds,
            properties,
            leadsByUuid,
            conversations,
            tab: tab || EConversationTab.ALL,
            search: search || '',
            filter: effectiveFilter,
            isAssigneeExempt: isUnreadFromOfflineTeammate,
        });
    }, [properties, leadsByUuid, conversations, isUnreadFromOfflineTeammate, tab, search, effectiveFilter, isDefaultInbox, inbox?.name, hiddenLeadLabelIds]);

    const conversationsByCategory = useMemo(() => {
        const byCategory = conversationCategories.reduce((acc, cat) => {
            if (!leads) {
                return { ...acc, [cat]: [] };
            }

            const conversationsWithArchiveStatus = filteredConversations.map(conversation => ({
                ...conversation,
                is_archived: leads.find(lead => lead.uuid === conversation.lead_uuid)?.is_archived || false,
            }));

            return {
                ...acc,
                [cat]: getConversationsByCategory(conversationsWithArchiveStatus, cat)
            };
        }, {} as Record<EConversationCategory, IConversation[]>);

        const unread = byCategory[EConversationCategory.Unread];
        byCategory[EConversationCategory.Unread] = unread.filter(
            c => !isUnreadFromOfflineTeammate(c, leadsByUuid?.get(c.lead_uuid))
        );
        byCategory[EConversationCategory.UnreadOffline] = unread.filter(
            c => isUnreadFromOfflineTeammate(c, leadsByUuid?.get(c.lead_uuid))
        );

        return byCategory;
    }, [filteredConversations, leads, leadsByUuid, isUnreadFromOfflineTeammate]);

    const propertiesById = useMemo(
        () => new Map(properties?.map(p => [p.id, p]) ?? []),
        [properties]
    );

    const sortedPropertyGroups = useMemo(() => {
        if (groupBy !== GroupByOption.PROPERTY) { return []; }

        const sorted = [...filteredConversations].sort((a, b) => {
            const aUnread = a.num_unread_messages > 0 ? 0 : 1;
            const bUnread = b.num_unread_messages > 0 ? 0 : 1;
            if (aUnread !== bUnread) { return aUnread - bUnread; }
            return new Date(b.last_contact_at).getTime() - new Date(a.last_contact_at).getTime();
        });

        const groups = new Map<number | null, IConversation[]>();
        for (const conv of sorted) {
            const key = conv.property_id ?? null;
            if (!groups.has(key)) { groups.set(key, []); }
            groups.get(key)!.push(conv);
        }
        return Array.from(groups.entries())
            .sort(([, a], [, b]) => b.length - a.length);
    }, [filteredConversations, groupBy]);

    const openConversation = useOpenConversation(filteredConversations, leadsByUuid);

    // Scroll to top effect
    useEffect(() => {
        let scrollTimer: NodeJS.Timeout;

        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const shouldShow = scrollTop > 300;

            setShowScrollTop(shouldShow);
            setIsScrolling(true);

            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                setIsScrolling(false);
            }, 2000);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimer);
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    useConversationDrawerFallbackRedirect();
    const { isEnabled: isDrawerEnabled, leadUuid: drawerLeadUuid, close: closeDrawer } = useConversationDrawer();

    // Loading state
    const isLoading = !loggedInUser ||
        !leads ||
        !properties ||
        !conversations ||
        !isInboxResolved ||
        !tab;

    if (isLoading) {
        return (
            <Page title="Reffie | Inbox">
                <LinearProgress sx={{ position: 'fixed', top: 0 }} />
            </Page>
        );
    }

    const hasConversations = !!conversations.length;

    return (
        <Page title="Reffie | Inbox">
            <ConversationDrawer open={isDrawerEnabled && !!drawerLeadUuid} onClose={closeDrawer} />
            {!hasConversations && <EmptyConversationsList />}
            {hasConversations && (
                <>
                    <KeyListener
                        selectedConversations={selectedConversations}
                        setSelectedConversations={setSelectedConversations}
                        openConversation={openConversation}
                        getFocusedConversation={() => focusedConversationRef.current}
                        isFilterOpen={isFilterOpen}
                        setFilterOpen={setFilterOpen}
                    />

                    <Box flex={1} className="conversations-page" flexDirection="column" flexWrap="nowrap" sx={{
                        minHeight: '100vh',
                        display: 'flex'
                    }}
                    >
                        <header>
                            <Header
                                searchScope="inbox"
                                filter={filter}
                                setFilter={setFilter}
                                search={search}
                                setSearch={setSearch}
                                resetFilter={resetFilter}
                                isFilterOpen={isFilterOpen}
                                setFilterOpen={setFilterOpen}
                                groupBy={groupBy}
                                setGroupBy={setGroupBy}
                            />
                            <StageTabs
                                tab={tab}
                                setTab={setTab}
                                filteredConversations={filteredConversations}
                                selectedConversations={selectedConversations}
                                setSelectedConversations={setSelectedConversations}
                            />
                        </header>

                        <Box flex={1} component="main" flexWrap="nowrap">
                            <List
                                tab={tab}
                                search={search}
                                filter={effectiveFilter}
                                conversationsByCategory={conversationsByCategory}
                                selectedConversations={selectedConversations}
                                setSelectedConversations={setSelectedConversations}
                                focusedConversation={focusedConversation}
                                setFocusedConversation={setFocusedConversation}
                                setMobileMenuAnchorEl={setMobileMenuAnchorEl}
                                groupBy={groupBy}
                                propertyGroups={sortedPropertyGroups}
                                propertiesById={propertiesById}
                            />
                            <Toolbar
                                selectedConversations={selectedConversations}
                                setSelectedConversations={setSelectedConversations}
                            />
                            <MobileMenu
                                leadsByUuid={leadsByUuid}
                                selectedConversations={selectedConversations}
                                setSelectedConversations={setSelectedConversations}
                                anchorEl={mobileMenuAnchorEl}
                                setAnchorEl={setMobileMenuAnchorEl}
                            />
                        </Box>
                        <ConversationsHotkeysFooter />
                    </Box>

                    {/* Scroll to Top Button */}
                    {showScrollTop && (
                        <Fab
                            onClick={scrollToTop}
                            size="medium"
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                            className={`${styles.scrollToTopButton} ${(isScrolling || isHovering) ? styles.visible : ''}`}
                        >
                            <Tooltip title="Scroll to top">
                                <ArrowUpToLine color="white" />
                            </Tooltip>
                        </Fab>
                    )}

                    {/* Invisible hover area */}
                    {showScrollTop && !isScrolling && !isHovering && (
                        <div
                            onMouseEnter={() => setIsHovering(true)}
                            className={styles.hoverArea}
                        />
                    )}
                </>
            )}
        </Page>
    );
}
